import React, { FC } from "react";
import { createPortal } from "react-dom";
import { LuBuilding2, LuUser } from "react-icons/lu";

import { Modal } from "../../../../../components/modal/Modal";
import { AddIndividualView } from "../../../../contacts/modals/addContact/AddIndividualView";
import AddOrganizationView from "../../../../contacts/modals/addContact/AddOrganizationView";
import { AddContactFormFrame } from "../../../../contacts/modals/addContact/components/AddContactFormFrame";
import { AddWorkContactOption } from "../model/addWork.types";
import { createdContactOption } from "../utils/addWork.helpers";

type Step = "choose" | "individual" | "organization";

type Props = {
  /** Which window is open; null = closed. */
  step: Step | null;
  setStep: (step: Step | null) => void;
  /** The new client, ready to be selected in the Contact box. */
  onCreated: (option: AddWorkContactOption) => void;
};

/**
 * "+ Create new contact" from the New work window's Contact box (QA batch 14,
 * NEWW-022/023): asks individual or organization, opens the app's normal New
 * individual / New organization window (without leaving the page) and selects
 * the new client in the Contact box.
 */
export const CreateContactFromWork: FC<Props> = ({
  step,
  setStep,
  onCreated,
}) => {
  if (!step) return null;
  const close = () => setStep(null);
  const created =
    (isOrganization: boolean) =>
    (contact: { customerId: string; name: string }) => {
      if (contact.customerId) {
        onCreated(
          createdContactOption(
            contact.customerId,
            contact.name,
            isOrganization,
          ),
        );
      }
      close();
    };

  let content: React.ReactNode;
  if (step === "choose") {
    content = (
      <Modal onClose={close} width={"360px"}>
        <div className={"text-[15px] font-bold text-[#1F2433]"}>
          Create new contact
        </div>
        <div className={"mt-1 text-[13px] text-[#6B7185]"}>
          What kind of client is it?
        </div>
        <div className={"mt-4 grid grid-cols-2 gap-3"}>
          {(
            [
              ["individual", "Individual", <LuUser key={"i"} size={18} />],
              [
                "organization",
                "Organization",
                <LuBuilding2 key={"o"} size={18} />,
              ],
            ] as const
          ).map(([value, label, icon]) => (
            <button
              key={value}
              type={"button"}
              onClick={() => setStep(value)}
              className={
                "flex flex-col items-center gap-2 rounded-xl border border-[#DCE2EA] px-3 py-4 text-[13.5px] font-semibold text-[#1F2433] hover:border-[#6B4EFF] hover:bg-[#F7F5FF]"
              }>
              <span className={"text-[#6B4EFF]"}>{icon}</span>
              {label}
            </button>
          ))}
        </div>
      </Modal>
    );
  } else if (step === "organization") {
    content = (
      <AddContactFormFrame onBack={close}>
        <AddOrganizationView
          isSearch
          closeModal={close}
          closeAddCustomerOrganizationPopupModal={close}
          onCreated={created(true)}
        />
      </AddContactFormFrame>
    );
  } else {
    content = (
      <AddContactFormFrame onBack={close}>
        <AddIndividualView
          isSearch
          closeModal={close}
          closeAddCustomerPopupModal={close}
          onCreated={created(false)}
        />
      </AddContactFormFrame>
    );
  }

  // Above the New work window (both use the app's modal layer).
  return createPortal(
    <div className={"relative z-[70]"}>{content}</div>,
    document.body,
  );
};
